<script setup>
import { useNow } from '@vueuse/core';
import { computed, watch } from 'vue';

const SECONDS = 1000;
const MINUTES = 60 * SECONDS;
const HOURS = 60 * MINUTES;
const DAYS = 24 * HOURS;

const props = defineProps({
    toDate: { type: [String, Date] },
    caption: { type: String },
    daysLabel: { type: String, default: 'days' },
    hoursLabel: { type: String, default: 'hours' },
    minutesLabel: { type: String, default: 'minutes' },
    secondsLabel: { type: String, default: 'seconds' },
    showDaysDigit: { type: Boolean, default: true },
    showHoursDigit: { type: Boolean, default: true },
    showMinutesDigit: { type: Boolean, default: true },
    showSecondsDigit: { type: Boolean, default: true },
})

const emit = defineEmits(['done']);

const { now, pause, resume } = useNow({ controls: true });

const remainingTime = computed(() => {
    const toDate = new Date(props.toDate);

    return toDate.getTime() - now.value.getTime();
})

let stopFn = () => {};
const stop = watch(
    remainingTime,
    () => {
        if (remainingTime.value <= 0) {
            pause()
            emit('done');
            stopFn()
        }
    },
    { immediate: true }
)
stopFn = stop;


const days = computed(() => (parseInt(remainingTime.value / DAYS)))
const hours = computed(() => (parseInt((remainingTime.value % (DAYS)) / HOURS)))
const minutes = computed(() => (parseInt((remainingTime.value % (HOURS)) / MINUTES)))
const seconds = computed(() => (parseInt((remainingTime.value % (MINUTES)) / SECONDS)))

const zeroPad = (value) => String(value).padStart(2,'0');
</script>

<template>
    <div class="countdown">
        <span v-if="caption" class="countdown__caption">
            {{ caption }}
        </span>
        <div class="countdown__display">
            <figure class="countdown__segment" v-if="showDaysDigit">
                <span class="countdown__value">{{zeroPad(days)}}</span>
                <figcaption class="countdown__label">
                    {{daysLabel}}
                </figcaption>
            </figure>
            <figure class="countdown__segment" v-if="showHoursDigit">
                <span class="countdown__value">{{zeroPad(hours)}}</span>
                <figcaption class="countdown__label">
                    {{hoursLabel}}
                </figcaption>
            </figure>
            <figure class="countdown__segment" v-if="showMinutesDigit">
                <span class="countdown__value">{{zeroPad(minutes)}}</span>
                <figcaption class="countdown__label">
                    {{minutesLabel}}
                </figcaption>
            </figure>
            <figure class="countdown__segment" v-if="showSecondsDigit">
                <span class="countdown__value">{{zeroPad(seconds)}}</span>
                <figcaption class="countdown__label">
                    {{secondsLabel}}
                </figcaption>
            </figure>
        </div>
    </div>
</template>

<style lang="scss">
@use "@/scss/framework" as *;

@layer components.countdown {

    :root {
        --countdown-segment-size: #{clamped(68px, 80px)};
        --countdown-segment-gap: #{clamped(10px, 25px)};
        --countdown-v-gap: 20px;
        --countdown-caption-font: 800 26px / 1 var(--font-family-headline);
        --countdown-caption-color: inherit;
        --countdown-value-color: var(--color-radical-red);
        --countdown-value-font: 800 #{clamped(57px, 67px)} / 1 var(--font-family-headline);
        --countdown-label-color: inherit;
        --countdown-label-font: normal 17px / 1.2 var(--font-family-subtitle);
    }

    .countdown {

        display: flex;
        flex-flow: column;
        place-items: center;
        place-content: center;
        gap: var(--countdown-v-gap);

        &__caption {
            font: var(--countdown-caption-font);
            color: var(--countdown-caption-color);
            text-align: center;
        }

        &__display {
            display: flex;
            flex-flow: row nowrap;
            place-items: center;
            place-content: center;
            gap: var(--countdown-segment-gap);
        }

        &__segment {
            width: var(--countdown-segment-size);
            height: var(--countdown-segment-size);
            background: hsl(0, 0%, 100%, 0.1);
            mix-blend-mode: normal;

            margin: 0;
            padding: 0;

            display: flex;
            flex-flow: column;
            place-content: center;
            place-items: center;
        }

        &__value {
            font: var(--countdown-value-font);
            color: var(--countdown-value-color);
        }

        &__label {
            font: var(--countdown-label-font);
            color: var(--countdown-label-color);
            text-align: center;

        }
    }
}
</style>
